/**
 * Approval Inbox — manager-facing list of pending approval requests
 * + a "Mine" tab so requesters can track their own filings + an
 * admin-only "All" tab for full visibility.
 *
 * Three columns of context per row:
 *   - Title + request_code + type label
 *   - Requester + reason + amount (if any)
 *   - Status badge + decided-by line for terminal rows
 *
 * The page is permission-aware client-side: non-eligible roles see
 * the Inbox tab empty (because the backend filters by role) but the
 * Mine tab still works for anyone who filed a request.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle2, XCircle, RefreshCw, Clock, Inbox, Send, ShieldCheck,
  AlertTriangle, ArchiveX,
} from 'lucide-react';
import { toast } from 'sonner';
import HelpInfo from '@/components/HelpInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Render a backend timestamp in Asia/Dubai time. Backend stores in
// UTC; naive ISO strings (no "Z" / "+00:00" suffix) would otherwise
// get parsed in the browser's local timezone, giving a 4-hour drift
// for users on the wrong locale. We normalise to UTC first, then
// format with explicit Asia/Dubai so the page reads consistently
// regardless of where the browser thinks it is.
const formatDateTime = (raw) => {
  if (!raw) return '—';
  let s = String(raw);
  if (s.includes('T') && !s.includes('+') && !s.endsWith('Z')) {
    s = `${s}Z`;  // naive → treat as UTC
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Asia/Dubai',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const STATUS_TONE = {
  pending:   'bg-amber-100 text-amber-800 border-amber-300',
  approved:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected:  'bg-red-100 text-red-800 border-red-300',
  withdrawn: 'bg-slate-100 text-slate-700 border-slate-300',
  expired:   'bg-slate-100 text-slate-500 border-slate-300',
};
const STATUS_ICON = {
  pending: Clock, approved: CheckCircle2, rejected: XCircle,
  withdrawn: ArchiveX, expired: AlertTriangle,
};

const isAdmin = (role) => {
  const r = (role || '').toLowerCase();
  return ['admin', 'owner', 'ceo', 'super_admin', 'account_owner']
    .some((h) => r.includes(h));
};

// CEO / owner-tier viewers sit at the top of the approval chain —
// they're the ones who decide, not the ones who file. For them the
// "Mine" tab shows requests they signed off on, not requests they
// submitted, and the "Request Approval" affordance is hidden across
// the app.
const isDecider = (role) => {
  const r = (role || '').toLowerCase();
  return ['ceo', 'owner', 'account_owner', 'super_admin']
    .some((h) => r.includes(h));
};

export default function ApprovalInbox() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState('inbox');
  const isCeoTier = isDecider(user?.role);
  // CEO/owner: the "Mine" tab queries the new `decided` scope so the
  // list shows what they've approved or rejected. Everyone else
  // keeps the original `mine` scope (what they submitted).
  const mineScope = isCeoTier ? 'decided' : 'mine';
  const mineLabel = isCeoTier ? 'My Decisions' : 'Mine';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-6xl mx-auto p-6 space-y-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
            <ShieldCheck size={26} className="text-violet-500" />
            Approvals
            <HelpInfo
              align="inline"
              title="How Approvals work"
              steps={[
                <>This is the central queue for anything in the CRM that requires sign-off before it can take effect — <strong>commission payouts</strong>, <strong>refunds</strong>, <strong>discount overrides</strong>, <strong>lead reassignments</strong>, <strong>booking cancellations</strong>, <strong>bulk actions</strong> above threshold, <strong>role changes</strong>, and any other workflow wired into the approval pipeline.</>,
                <><strong>Where requests come from:</strong> they're created when someone hits <em>Submit for Approval</em> from inside a workflow — e.g. <em>Contact &gt; ⋯ More &gt; Request Approval</em>, the discount field on a deal, the refund flow on a booking, or auto-triggered when a bulk action exceeds the safety threshold. Each one carries enough context (related record, amount, reason) for the approver to decide without leaving the page.</>,
                <><strong>Inbox</strong> — items waiting on <em>your</em> decision based on your role. Click <em>Approve</em> to release the action, or <em>Reject</em> (a note is required) to send it back to the requester.</>,
                isCeoTier
                  ? <><strong>My Decisions</strong> — requests you've approved or rejected, sorted by most recent first. Useful for auditing your own sign-offs.</>
                  : <><strong>Mine</strong> — requests you've submitted. Track their status here: <span className="text-amber-700">pending</span>, <span className="text-emerald-700">approved</span>, <span className="text-red-700">rejected</span>, withdrawn, or expired. You can <em>withdraw</em> a pending request you filed by mistake — once decided, it's locked.</>,
                <><strong>All</strong> (admins only) — every request across the organisation, useful for auditing and reporting.</>,
                <><strong>Who approves what:</strong> commission, discount and lead-reassignment requests go to <strong>HOD / managers</strong>. Refunds, bulk actions and role changes require an <strong>admin / CEO</strong>. The eligible role is set per request type (admin-configurable).</>,
                <><strong>Auto-expiry:</strong> requests sit in the inbox for a fixed window before they auto-expire and the requester is notified — bulk actions <strong>2 days</strong>, lead reassignment <strong>3 days</strong>, discount <strong>7 days</strong>, commission / refund / role change <strong>14 days</strong>. The requester can refile if it expired before being decided.</>,
                <><strong>Notifications:</strong> when a new request lands in your inbox, you get an in-app bell notification. When your own request is approved, rejected, or expires, you're notified too — no need to keep polling this page.</>,
                <>Each card shows the request <em>title</em>, a unique <em>request code</em> (e.g. <code>A-1042</code>) for cross-referencing, the <em>requester</em>, an <em>amount</em> if relevant (e.g. refund value), and the original <em>reason</em>. Terminal rows also show who decided, when, and any note they left.</>,
              ]}
              tip="Rejections require a short note explaining why — the requester sees it and uses it to fix and refile. Quote the request code (A-1042) in any chat / email about the decision."
            />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Review and act on approval requests across the team.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="inbox">
              <Inbox size={14} className="mr-1.5" /> Inbox
            </TabsTrigger>
            <TabsTrigger value="mine">
              <Send size={14} className="mr-1.5" /> {mineLabel}
            </TabsTrigger>
            {isAdmin(user?.role) && (
              <TabsTrigger value="all">
                <ShieldCheck size={14} className="mr-1.5" /> All
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="inbox">
            <ApprovalList scope="inbox" token={token} userRole={user?.role} />
          </TabsContent>
          <TabsContent value="mine">
            <ApprovalList scope={mineScope} token={token} userRole={user?.role} />
          </TabsContent>
          {isAdmin(user?.role) && (
            <TabsContent value="all">
              <ApprovalList scope="all" token={token} userRole={user?.role} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

const ApprovalList = ({ scope, token, userRole }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisionTarget, setDecisionTarget] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/approvals/requests`, {
        params: { token, scope },
      });
      // Defensive client-side sort by most-recent-activity, in case
      // any row is missing updated_at on the server (legacy data).
      // Uses decided_at if present, then updated_at, then created_at.
      const list = (res.data?.data?.requests || []).slice().sort((a, b) => {
        const ka = a.decided_at || a.updated_at || a.created_at || '';
        const kb = b.decided_at || b.updated_at || b.created_at || '';
        return kb.localeCompare(ka);  // ISO strings sort lexicographically
      });
      setRows(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load approvals');
    } finally {
      setLoading(false);
    }
  }, [token, scope]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">
            {scope === 'inbox'   ? 'Pending — awaiting your decision' :
             scope === 'mine'    ? 'Your requests' :
             scope === 'decided' ? 'Decisions you signed off on' :
                                   'All approval requests'}
          </CardTitle>
          <CardDescription>
            {scope === 'inbox'
              ? "Click approve or reject. A note is required on reject."
              : scope === 'mine'
                ? "Track status of requests you've submitted. Withdraw any pending ones."
                : scope === 'decided'
                  ? "Audit trail of every approval / rejection you've made, most recent first."
                  : "Org-wide audit view (admin only)."}
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-8">
          <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && rows.length === 0 ? (
          <Skeleton className="h-24 w-full" />
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <ShieldCheck size={32} className="mx-auto text-emerald-400 mb-2" />
            {scope === 'inbox'
              ? "Nothing pending — your approval inbox is clear."
              : "No requests to show."}
          </div>
        ) : (
          rows.map((r) => (
            <ApprovalRow
              key={r.id}
              row={r}
              scope={scope}
              userRole={userRole}
              onAct={(action) => setDecisionTarget({ row: r, action })}
              onChanged={load}
              token={token}
            />
          ))
        )}
      </CardContent>

      <DecisionDialog
        target={decisionTarget}
        token={token}
        onClose={() => setDecisionTarget(null)}
        onCompleted={() => { setDecisionTarget(null); load(); }}
      />
    </Card>
  );
};

const ApprovalRow = ({ row, scope, userRole, onAct, onChanged, token }) => {
  const StatusIcon = STATUS_ICON[row.status] || Clock;
  const canActOnInbox = scope === 'inbox' && row.status === 'pending';
  const canWithdraw = scope === 'mine' && row.status === 'pending';
  const created = formatDateTime(row.created_at);
  const decided = row.decided_at ? formatDateTime(row.decided_at) : null;

  const withdraw = async () => {
    if (!window.confirm('Withdraw this request? You can re-submit later.')) return;
    try {
      await axios.post(
        `${API}/approvals/requests/${row.id}/withdraw?token=${token}`,
        { note: 'Withdrawn by requester' },
      );
      toast.success('Request withdrawn');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not withdraw');
    }
  };

  return (
    <div className={`border rounded-md p-3 ${
      row.status === 'pending'
        ? 'border-amber-200 bg-amber-50/30'
        : 'border-slate-200 dark:border-slate-700'
    }`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{row.title}</span>
            <Badge variant="outline" className="text-[10px]">
              {row.request_code}
            </Badge>
            <Badge className={`text-[10px] border ${STATUS_TONE[row.status] || ''}`}>
              <StatusIcon size={10} className="mr-1 inline" />
              {row.status}
            </Badge>
            <span className="text-xs text-slate-400">
              {(row.request_type || '').replace(/_/g, ' ')}
            </span>
          </div>

          <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
            Requested by <strong>{row.requester_name}</strong> · {created}
            {row.amount !== null && row.amount !== undefined && (
              <span> · amount <strong>AED {Number(row.amount).toLocaleString()}</strong></span>
            )}
          </div>

          {row.reason && (
            <p className="mt-1 text-xs italic text-slate-700 dark:text-slate-300">
              "{row.reason}"
            </p>
          )}

          {decided && row.status !== 'pending' && (
            <p className="mt-1 text-xs text-slate-500">
              {row.status === 'expired'
                ? `Auto-expired ${decided}`
                : `${row.status === 'approved' ? '✓' : '✕'} by ${row.decided_by_name || '—'} · ${decided}`}
              {row.decision_note && <span> — {row.decision_note}</span>}
            </p>
          )}
        </div>

        <div className="flex gap-1.5 flex-shrink-0">
          {canActOnInbox && (
            <>
              <Button
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white"
                onClick={() => onAct('approve')}
              >
                <CheckCircle2 size={13} className="mr-1" /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => onAct('reject')}
              >
                <XCircle size={13} className="mr-1" /> Reject
              </Button>
            </>
          )}
          {canWithdraw && (
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={withdraw}
            >
              <ArchiveX size={13} className="mr-1" /> Withdraw
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const DecisionDialog = ({ target, token, onClose, onCompleted }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (target) setNote(''); }, [target]);

  if (!target) return null;
  const isReject = target.action === 'reject';
  const submit = async () => {
    if (isReject && !note.trim()) {
      toast.error('A note is required when rejecting');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/approvals/requests/${target.row.id}/${target.action}?token=${token}`,
        { note: note.trim() || null },
      );
      toast.success(isReject ? 'Rejected' : 'Approved');
      onCompleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${target.action}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {isReject ? 'Reject request' : 'Approve request'} — {target.row.request_code}
          </DialogTitle>
          <DialogDescription>
            {target.row.title}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={isReject ? "Reason for rejection (required)" : "Optional note for the requester"}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            className={isReject ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}
            onClick={submit}
            disabled={busy || (isReject && !note.trim())}
          >
            {busy ? 'Saving…' : isReject ? 'Reject' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
